/**
 * Right-anchored side drawer. Unlike MobileBottomSheet:
 *  - No backdrop scrim — the rest of the app stays interactive while open.
 *  - Slides in from the right, takes ~420px on desktop.
 *  - On mobile (≤768px), takes ~92vw so a strip of background is visible to
 *    show context. Tap the strip to dismiss.
 *  - Esc closes (so users can navigate back to the app via keyboard too).
 *  - Optional `minimized` mode collapses to a thin floating bar showing a
 *    summary; click to re-expand. Used by the "log steps to replicate"
 *    recording flow so the form gets out of the user's way.
 *
 * Z-index: 1450 (just below the floating bug button at 1500). The drawer
 * stays above general page content but doesn't obstruct the floating
 * surfaces at the same plane.
 *
 * Usage:
 *   <SideDrawer open={open} onClose={...} title="Report" minimized={...}>
 *     ...content...
 *   </SideDrawer>
 */

import React from 'react';
import { setBugReportDrawerOpen } from '../bugReportDrawerState.js';

const isDesktopWide = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(min-width: 769px)').matches;

export default function SideDrawer({
  open,
  onClose,
  title,
  minimized,
  onExpandFromMinimized,
  minimizedSummary,
  children,
}) {
  const [wide, setWide] = React.useState(isDesktopWide);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(min-width: 769px)');
    const handler = (e) => setWide(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Publish open/close to the global drawer-state store so other floating
  // surfaces (DM Quick Journal cluster, future stat card) can shift their
  // layout to make room. Reset to false on unmount as well to avoid a
  // stale 'true' if the drawer unmounts mid-render.
  React.useEffect(() => {
    setBugReportDrawerOpen(!!open);
    return () => setBugReportDrawerOpen(false);
  }, [open]);

  // Esc closes (or expands from minimized) so keyboard users have a way
  // out without clicking the close button.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (minimized) {
        e.stopPropagation();
        onExpandFromMinimized?.();
      } else {
        e.stopPropagation();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, minimized, onExpandFromMinimized]);

  if (!open) return null;

  const width = wide ? 440 : '92vw';

  // Minimized bar — rendered ALONGSIDE (not instead of) the main drawer
  // when minimized=true. The drawer itself is set to display:none so its
  // children stay mounted and preserve their state. Switching the drawer
  // out for the bar entirely (the previous implementation) unmounted
  // ComposeForm, wiping the user's title / description / screenshots
  // every time they hit "Log steps" or "Select element".
  const minimizedBar = minimized ? (
    <div
      onClick={onExpandFromMinimized}
      role="button"
      title="Expand bug-report panel"
      data-bug-report-drawer
      style={{
        position: 'fixed',
        bottom: '16px',
        right: '16px',
        zIndex: 10001, // above the (hidden) main drawer + the floating button
        padding: '10px 14px',
        background: 'linear-gradient(135deg, #6b1e1e 0%, #3a1414 100%)',
        border: '2px solid #ff6961',
        borderRadius: 10,
        color: '#ffd0cc',
        cursor: 'pointer',
        boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
        fontSize: '0.78rem',
        maxWidth: 320,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ fontSize: '1rem' }}>🪲</span>
      <span style={{ flex: 1 }}>{minimizedSummary || 'Bug report (collapsed)'}</span>
      <span style={{ color: '#ff6961', fontSize: '0.62rem' }}>▶ Expand</span>
    </div>
  ) : null;

  return (<>
    {minimizedBar}
    <div
      role="dialog"
      aria-label={title || 'Side panel'}
      // Marker attribute lets the bug-report recorder filter out clicks
      // that happen inside the form itself (we don't want to capture the
      // user clicking around our own UI as part of their repro steps).
      data-bug-report-drawer
      // Stops bubble propagation so clicks inside the drawer don't bubble
      // to the page underneath. Outside the drawer, the page stays
      // interactive — that's the whole point.
      onClick={(e) => e.stopPropagation()}
      style={{
        // Anchored at the BOTTOM-right (above the floating icon row at
        // bottom: 16). The drawer grows upward as content gets taller,
        // limited by `maxHeight`.
        //
        // When `minimized` is true we set display:none rather than
        // unmounting — that keeps ComposeForm's state (title, description,
        // screenshots) intact across recording / picker activations.
        position: 'fixed',
        // bottom:80 sits the drawer at the "row above the bottom row" —
        // close to the journal panel's default anchor at bottom:70 so
        // every panel (drawer / journal / stat) shares roughly the same
        // vertical surface. Icons drop into the bottom row at bottom:20
        // (top edge at 68) and the drawer's bottom (vp_y = vh-80) sits
        // 12px above the icons' top — clean separation.
        bottom: 80,
        right: 12,
        maxHeight: 'calc(100vh - 92px)',
        width,
        background: 'var(--bg2, #1e1624)',
        border: '2px solid rgba(201,162,39,0.35)',
        borderRadius: 14,
        boxShadow: '0 -4px 28px rgba(0,0,0,0.55), 0 8px 32px rgba(0,0,0,0.45)',
        zIndex: 10000,
        display: minimized ? 'none' : 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        animation: minimized ? undefined : 'sideDrawerSlideIn 220ms cubic-bezier(0.34, 1.2, 0.64, 1)',
        // The animation rule below is keyed via a <style> injection; without
        // CSS modules or a bundler-aware loader we inline the keyframes once
        // per mount of any drawer instance. Idempotent: same name === same
        // rule.
      }}
    >
      <style>{`@keyframes sideDrawerSlideIn { from { transform: translate(calc(100% + 16px), 8px); } to { transform: translate(0, 0); } }`}</style>

      {/* Header bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 14px',
          borderBottom: '1px solid rgba(201,162,39,0.2)',
          background: 'linear-gradient(180deg, rgba(201,162,39,0.08), transparent)',
          flexShrink: 0,
        }}
      >
        <span style={{ flex: 1, fontSize: '0.95rem', fontWeight: 600, color: '#c9a227' }}>
          {title}
        </span>
        <button
          onClick={onClose}
          title="Close (Esc)"
          style={{
            background: 'rgba(255,105,97,0.12)',
            border: '1px solid rgba(255,105,97,0.55)',
            borderRadius: 5,
            color: '#ff6961',
            cursor: 'pointer',
            padding: '5px 12px',
            fontSize: '0.85rem',
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          ✕ Close
        </button>
      </div>

      {/* Body — scrolls independently. NOT flex:1; sizes to content so a
          short form gets a short drawer rather than oceans of empty space. */}
      <div style={{ overflowY: 'auto', padding: '12px 14px 18px', minHeight: 0 }}>
        {children}
      </div>
    </div>
  </>);
}
